import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Icon as IconifyIcon } from '@iconify/react';
import { useI18n } from '../../lib/i18n';

export default function SecuritySectionSection3() {
  const [activeTerminal, setActiveTerminal] = useState<1 | 2>(1);
  const { t } = useI18n()

  return (
    <section className="relative bg-white py-20 lg:py-32">
      {/* Background Texture: Subtle dots for technical precision */}
      <div className="absolute inset-0 bg-[radial-gradient(#0052CC_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.03] pointer-events-none overflow-hidden" />

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">

          {/* LEFT COLUMN: Sticky Content */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="space-y-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#0052CC]/5 text-[#0052CC] text-xs font-bold tracking-wider uppercase mb-6">
                  {t.security.badge}
                </span>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#172B4D] mb-6 leading-tight">
                  {t.security.title}
                </h2>
                <div className="text-lg text-[#172B4D]/70 space-y-6 leading-relaxed">
                  <p>
                    {t.security.p1}
                  </p>
                  <p>
                    {t.security.p2} <strong className="text-[#172B4D] font-semibold">{t.security.p2bold}</strong> {t.security.p2rest}
                  </p>
                  <p>
                    {t.security.p3}
                  </p>
                </div>

                {/* Trust Indicators List */}
                <ul className="mt-8 space-y-4">
                  {[
                    t.security.insuredByLaw,
                    t.security.pavedParking,
                    t.security.only9km
                  ].map((item, index) => (
                    <motion.li 
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + (index * 0.1) }}
                      className="flex items-center gap-3 text-[#172B4D] font-medium"
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0052CC]/10 flex items-center justify-center text-[#0052CC]">
                        <IconifyIcon icon="ph:shield-check-fill" className="w-4 h-4" />
                      </div>
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>

          {/* RIGHT COLUMN: Scrollable Cards */}
          <div className="flex flex-col gap-8">
            
            {/* CARD A: Location / Parking */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-[#0052CC]/30 transition-all duration-300"
            >
              <div className="aspect-[16/9] overflow-hidden relative">
                <img
                  src="/images/secure-parking.png"
                  alt="Sicherer Parkplatz"
                  
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#172B4D]/60 to-transparent opacity-60" />
                
                {/* Overlay Badge */}
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2 border border-white/20">
                  <IconifyIcon icon="ph:map-pin-fill" className="text-[#0052CC]" />
                  <span className="text-xs font-bold text-[#172B4D] uppercase tracking-wide">{t.security.distanceBadge}</span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="text-xs font-bold uppercase tracking-wider text-[#0052CC] mb-2">{t.security.locationLabel}</div>
                <h3 className="text-xl font-semibold text-[#172B4D] mb-2">{t.security.locationTitle}</h3>
                <p className="text-[#172B4D]/70 text-sm leading-relaxed">
                  {t.security.locationDesc}
                </p>
              </div>
            </motion.div>

            {/* CARD B: Insurance (Abstract UI) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="group bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm hover:shadow-md hover:border-[#0052CC]/30 transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row gap-6 items-center">
                {/* Animated Shield UI */}
                <div className="relative w-24 h-24 flex-shrink-0 flex items-center justify-center">
                  {/* Pulsing Background */}
                  <div className="absolute inset-0 bg-[#00BCC5]/10 rounded-full animate-pulse" />
                  
                  {/* Progress Ring SVG */}
                  <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="46" fill="none" stroke="#E2E8F0" strokeWidth="4" />
                    <motion.circle 
                      cx="50" cy="50" r="46" 
                      fill="none" 
                      stroke="#00BCC5" 
                      strokeWidth="4"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                    />
                  </svg>
                  
                  {/* Icon */}
                  <IconifyIcon icon="ph:shield-check-fill" className="w-10 h-10 text-[#00BCC5] relative z-10" />
                </div>

                <div className="flex-1 text-center md:text-left">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#0052CC] mb-2">{t.security.securityLabel}</div>
                  <h3 className="text-xl font-semibold text-[#172B4D] mb-2">{t.security.securityTitle}</h3>
                  <p className="text-[#172B4D]/70 text-sm leading-relaxed">
                    {t.security.securityDesc}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CARD C: Handover (Interactive) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-[#0052CC]/30 transition-all duration-300"
            >
              <div className="aspect-[16/9] overflow-hidden relative">
                <img
                  src="/images/valet-key-handover.png"
                  alt="Schlüsselübergabe"
                  
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#172B4D]/80 via-transparent to-transparent opacity-80" />
                
                {/* Interactive Toggle */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md rounded-lg p-1 flex shadow-lg border border-white/20">
                  <button 
                    onClick={() => setActiveTerminal(1)}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-200 ${
                      activeTerminal === 1 
                      ? 'bg-[#0052CC] text-white shadow-sm' 
                      : 'text-[#172B4D]/60 hover:text-[#172B4D]'
                    }`}
                  >
                    Terminal 1
                  </button>
                  <button 
                    onClick={() => setActiveTerminal(2)}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-200 ${
                      activeTerminal === 2 
                      ? 'bg-[#0052CC] text-white shadow-sm' 
                      : 'text-[#172B4D]/60 hover:text-[#172B4D]'
                    }`}
                  >
                    Terminal 2
                  </button>
                </div>

                {/* Status Indicator */}
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-[#00BCC5]/20 backdrop-blur-md border border-[#00BCC5]/30 px-3 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-[#00BCC5] animate-pulse" />
                    <span className="text-xs font-bold text-white tracking-wide">
                      {activeTerminal === 1 ? t.security.terminal1Active : t.security.terminal2Active}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="text-xs font-bold uppercase tracking-wider text-[#0052CC] mb-2">{t.security.handoverLabel}</div>
                <h3 className="text-xl font-semibold text-[#172B4D] mb-2">{t.security.handoverTitle}</h3>
                <p className="text-[#172B4D]/70 text-sm leading-relaxed">
                  {t.security.handoverDesc}
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  );
}